import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  PhotoIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { speciesApi, type Species, type CreateSpeciesDto } from '../api/species';
import { IUCNStatus, CITESStatus } from '../utils/enums';
import { asArray } from '../utils/asArray';
import AddSpeciesModal from '../components/AddSpeciesModal';
import EditSpeciesModal from '../components/EditSpeciesModal';
import SpeciesPhotoGallery from '../components/species/SpeciesPhotoGallery';
import SpeciesEnrichmentModal from '../components/species/SpeciesEnrichmentModal';
import { getPhotoUrl } from '../api/photos';
import Avatar from '../components/common/Avatar';
import { useGlobalConfig } from '../contexts/GlobalConfigContext';
import { formatSexualMaturityMonths, formatIncubationDays, displayText, formatDays, formatYears, formatPriceRange, formatAiLastUpdated, TAXONOMY_RANKS } from '../utils/speciesDisplayUtils';
import PageHero from '../components/ui/PageHero';
import SortOrderButton from '../components/ui/SortOrderButton';
import EmptyState from '../components/ui/EmptyState';
import SpeciesMutationCatalogPanel from '../components/genetics/SpeciesMutationCatalogPanel';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { daysAgo, type SpeciesListFilter } from '../utils/listFilterPaths';

interface SpeciesFilters {
  iucnStatus?: IUCNStatus;
  citesStatus?: CITESStatus;
  typeCategory?: string;
  originLocation?: string;
  focus?: SpeciesListFilter;
}

// Helper function to get avatar URL from species photos
const getSpeciesAvatarUrl = (species: Species): string | null => {
  if (!species.photos || species.photos.length === 0) {
    return null;
  }
  
  // Find the photo marked as avatar
  const avatarPhoto = species.photos.find((photo: any) => photo.isAvatar);
  if (avatarPhoto) {
    return getPhotoUrl(avatarPhoto.url);
  }
  
  // Fallback to avatarPhotoId if available
  if (species.avatarPhotoId) {
    return getPhotoUrl(species.avatarPhotoId);
  }
  
  return null;
};

const getErrorText = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== 'object') {
    return error instanceof Error ? error.message : fallback;
  }

  const apiError = error as {
    data?: { message?: string | string[]; error?: string };
    message?: string;
  };
  const message = apiError.data?.message;
  if (Array.isArray(message) && message.length > 0) {
    return message.join(', ');
  }
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  if (typeof apiError.data?.error === 'string' && apiError.data.error.trim()) {
    return apiError.data.error;
  }
  if (typeof apiError.message === 'string' && apiError.message.trim()) {
    return apiError.message;
  }

  return fallback;
};

export default function Species() {
  const { config } = useGlobalConfig();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Data state
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SpeciesFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Species>('commonName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEnrichModal, setShowEnrichModal] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState<Species | null>(null);

  useEffect(() => {
    loadSpecies();
  }, []);

  // Apply dashboard / deep-link query filters once on mount / when URL changes
  useEffect(() => {
    const focus = searchParams.get('filter') as SpeciesListFilter | null;
    const iucnStatus = searchParams.get('iucnStatus') as IUCNStatus | null;
    const next: SpeciesFilters = {};
    if (focus === 'recent' || focus === 'attention' || focus === 'conservation' || focus === 'withPhotos') {
      next.focus = focus;
    }
    if (iucnStatus && Object.values(IUCNStatus).includes(iucnStatus)) {
      next.iucnStatus = iucnStatus;
    }
    if (Object.keys(next).length > 0) {
      setFilters(next);
      setShowFilters(true);
      if (focus === 'recent') {
        setSortBy('createdAt' as keyof Species);
        setSortOrder('desc');
      }
    }
  }, [searchParams]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filters]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: config?.currency?.default || 'ZAR',
    }).format(amount);
  };

  const loadSpecies = async () => {
    setLoading(true);
    const attempts = 5;
    try {
      for (let attempt = 0; attempt < attempts; attempt++) {
        try {
          const response = await speciesApi.getAll();
          setSpecies(asArray(response.data));
          setError(null);
          return;
        } catch (err) {
          const msg = err instanceof Error ? err.message : '';
          const waking = /starting|timeout|network|fetch|abort/i.test(msg);
          if (waking && attempt < attempts - 1) {
            await new Promise((resolve) => setTimeout(resolve, 2000 * (attempt + 1)));
            continue;
          }
          setError('Failed to load species');
          console.error('Error loading species:', err);
          toast.error(waking ? 'Your instance is still starting. Try again in a few seconds.' : 'Failed to load species');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Safe string for search (handles null/undefined)
  const searchable = (s: Species, term: string) => {
    const t = term.toLowerCase();
    return (
      (s.commonName ?? '').toLowerCase().includes(t) ||
      (s.genus ?? '').toLowerCase().includes(t) ||
      (s.species ?? '').toLowerCase().includes(t) ||
      (s.fullTaxonomicName ?? '').toLowerCase().includes(t) ||
      (s.typeCategory ?? '').toLowerCase().includes(t) ||
      (s.originLocation ?? '').toLowerCase().includes(t)
    );
  };

  // Filter and search logic
  const filteredSpecies = useMemo(() => {
    let result = species;

    // Apply search filter
    if (searchTerm.trim()) {
      result = result.filter((s) => searchable(s, searchTerm));
    }

    // Apply filters
    if (filters.iucnStatus) {
      result = result.filter((s) => s.iucnStatus === filters.iucnStatus);
    }
    if (filters.citesStatus) {
      result = result.filter((s) => s.citesStatus === filters.citesStatus);
    }
    if (filters.typeCategory) {
      result = result.filter((s) => s.typeCategory === filters.typeCategory);
    }
    if (filters.originLocation) {
      result = result.filter((s) => s.originLocation === filters.originLocation);
    }

    if (filters.focus === 'recent') {
      const cutoff = daysAgo(90);
      result = result.filter((s) => new Date(s.createdAt || s.updatedAt || 0) >= cutoff);
    } else if (filters.focus === 'attention') {
      result = result.filter(
        (s) =>
          s.iucnStatus === IUCNStatus.CRITICALLY_ENDANGERED ||
          s.iucnStatus === IUCNStatus.ENDANGERED ||
          !s.avatarPhotoId,
      );
    } else if (filters.focus === 'conservation') {
      result = result.filter(
        (s) =>
          s.iucnStatus === IUCNStatus.CRITICALLY_ENDANGERED ||
          s.iucnStatus === IUCNStatus.ENDANGERED ||
          s.iucnStatus === IUCNStatus.VULNERABLE,
      );
    } else if (filters.focus === 'withPhotos') {
      result = result.filter((s) => (s.photos?.length || 0) > 0 || !!s.avatarPhotoId);
    }

    // Apply sorting
    result.sort((a, b) => {
      const aVal = a[sortBy];
      const bVal = b[sortBy];
      
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [species, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredSpecies.length / itemsPerPage);
  const paginatedSpecies = filteredSpecies.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const typeCategories = [...new Set(species.map((s) => s.typeCategory))];
  const originLocations = [...new Set(species.map((s) => s.originLocation))];

  // Event handlers
  const handleAddSpecies = async (data: CreateSpeciesDto) => {
    try {
      await speciesApi.create(data);
      await loadSpecies();
      setShowAddModal(false);
      toast.success('Species added successfully');
    } catch (error) {
      const message = getErrorText(error, 'Failed to add species');
      console.error('Error adding species:', message, error);
      toast.error(message);
    }
  };

  const handleEditSpecies = async (id: string, data: Partial<Species>) => {
    try {
      await speciesApi.update(id, data);
      await loadSpecies();
      setShowEditModal(false);
      setSelectedSpecies(null);
      toast.success('Species updated successfully');
    } catch (error) {
      console.error('Error updating species:', error);
      toast.error(getErrorText(error, 'Failed to update species'));
    }
  };

  const handleDeleteSpecies = async () => {
    if (!selectedSpecies) return;
    
    try {
      await speciesApi.delete(selectedSpecies.id);
      await loadSpecies();
      setShowDeleteModal(false);
      setSelectedSpecies(null);
      toast.success('Species deleted successfully');
    } catch (error) {
      console.error('Error deleting species:', error);
      toast.error(getErrorText(error, 'Failed to delete species'));
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const focusLabel =
    filters.focus === 'recent'
      ? 'Recently added (last 90 days)'
      : filters.focus === 'attention'
        ? 'Species needing attention'
        : filters.focus === 'conservation'
          ? 'Conservation concern (CR / EN / VU)'
          : filters.focus === 'withPhotos'
            ? 'Species with photos'
            : null;

  const openEditModal = (species: Species) => {
    setSelectedSpecies(species);
    setShowEditModal(true);
  };

  const openDeleteModal = (species: Species) => {
    setSelectedSpecies(species);
    setShowDeleteModal(true);
  };

  const openViewModal = (species: Species) => {
    setSelectedSpecies(species);
    setShowViewModal(true);
  };

  const openPhotoModal = (species: Species) => {
    setSelectedSpecies(species);
    setShowPhotoModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <PageHero
        title="Species Management"
        subtitle="Manage your bird species database with photos, detailed information, and analytics."
        actions={
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowEnrichModal(true)}
              className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
            >
              <SparklesIcon className="-ml-0.5 mr-1.5 h-5 w-5 text-cyan-600" aria-hidden="true" />
              Complete Data
            </button>
            <button
              type="button"
              onClick={() => setShowAddModal(true)}
              className="btn btn-primary focus-pop"
            >
              <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 inline" aria-hidden="true" />
              Add Species
            </button>
          </div>
        }
      />

      {focusLabel && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm text-cyan-900">
          <span>
            Showing: <span className="font-semibold">{focusLabel}</span>
          </span>
          <button type="button" onClick={clearFilters} className="font-medium text-cyan-700 hover:text-cyan-900">
            Clear filter
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mt-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
              <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
                <MagnifyingGlassIcon className="h-5 w-5" />
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
                placeholder="Search species..."
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
            >
              <FunnelIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
            <button
              type="button"
              onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
              className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
            >
              {viewMode === 'grid' ? 'Table View' : 'Grid View'}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-slate-50/80 p-4 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">IUCN Status</label>
                <select
                  value={filters.iucnStatus || ''}
                  onChange={(e) => setFilters({ ...filters, iucnStatus: e.target.value as IUCNStatus || undefined })}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                >
                  <option key="empty-option-1753461297399" value="">All Statuses</option>
                  {Object.values(IUCNStatus).map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">CITES Status</label>
                <select
                  value={filters.citesStatus || ''}
                  onChange={(e) => setFilters({ ...filters, citesStatus: e.target.value as CITESStatus || undefined })}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                >
                  <option key="empty-option-1753461297399" value="">All Statuses</option>
                  {Object.values(CITESStatus).map((status) => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Type Category</label>
                <select
                  value={filters.typeCategory || ''}
                  onChange={(e) => setFilters({ ...filters, typeCategory: e.target.value || undefined })}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                >
                  <option key="empty-option-1753461297399" value="">All Categories</option>
                  {typeCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">Origin Location</label>
                <select
                  value={filters.originLocation || ''}
                  onChange={(e) => setFilters({ ...filters, originLocation: e.target.value || undefined })}
                  className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                >
                  <option key="empty-option-1753461297399" value="">All Locations</option>
                  {originLocations.map((location) => (
                    <option key={location} value={location}>{location}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Results Summary */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-slate-700">
          Showing {paginatedSpecies.length} of {filteredSpecies.length} species
        </p>
        <div className="flex items-center space-x-2">
          <label className="text-sm text-slate-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as keyof Species)}
            className="rounded-md border-gray-300 text-sm"
          >
            <option key="commonName-1753461297399" value="commonName">Common Name</option>
            <option key="genus-1753461297399" value="genus">Genus</option>
            <option key="species-1753461297399" value="species">Species</option>
            <option key="typeCategory-1753461297399" value="typeCategory">Type Category</option>
            <option key="originLocation-1753461297399" value="originLocation">Origin Location</option>
          </select>
          <SortOrderButton
            order={sortOrder}
            onToggle={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50"
          />
        </div>
      </div>

      {/* Species Grid/Table */}
      <div className="mt-6">
        {viewMode === 'grid' ? (
          <div className="fade-up grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {paginatedSpecies.map((species) => (
              <div
                key={species.id}
                className="bg-white overflow-hidden shadow rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                  {getSpeciesAvatarUrl(species) ? (
                    <Avatar
                      photo={getSpeciesAvatarUrl(species)}
                      size="xl"
                      shape="rounded"
                      objectFit="cover"
                      objectPosition="center"
                      autoCrop={true}
                      className="w-full h-48"
                      alt={species.commonName}
                      fallbackText={species.commonName}
                    />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 flex items-center justify-center">
                      <PhotoIcon className="h-12 w-12 text-slate-400" />
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-medium text-slate-900 truncate">
                    {species.commonName}
                  </h3>
                  <p className="text-sm text-slate-500 italic truncate">
                    {species.fullTaxonomicName}
                  </p>
                  <p className="text-sm text-slate-500 truncate mt-1">
                    {species.originLocation}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex space-x-1">
                      <span className="brand-chip-info">
                        {species.iucnStatus}
                      </span>
                      <span className="brand-chip-success">
                        {species.citesStatus}
                      </span>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => openViewModal(species)}
                        className="text-slate-400 hover:text-cyan-600"
                        title="View Details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openPhotoModal(species)}
                        className="text-slate-400 hover:text-cyan-600"
                        title="View Photos"
                      >
                        <PhotoIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(species)}
                        className="text-slate-400 hover:text-cyan-600"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => openDeleteModal(species)}
                        className="text-slate-400 hover:text-red-600"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="fade-up bg-white shadow overflow-hidden sm:rounded-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-slate-50/80">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Species
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Scientific Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Origin
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="relative px-6 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedSpecies.map((species) => (
                  <tr key={species.id} className="hover:bg-slate-50/80">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {getSpeciesAvatarUrl(species) ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={getSpeciesAvatarUrl(species) || `https://ui-avatars.com/api/?name=${encodeURIComponent(species.commonName)}&background=e5e7eb&color=374151&size=40`}
                              alt={species.commonName}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(species.commonName)}&background=e5e7eb&color=374151&size=40`;
                              }}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <PhotoIcon className="h-6 w-6 text-slate-400" />
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-slate-900">
                            {species.commonName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {species.fullTaxonomicName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {species.typeCategory}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      {species.originLocation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                      <div className="flex space-x-1">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-cyan-100 text-cyan-800">
                          {species.iucnStatus}
                        </span>
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-800">
                          {species.citesStatus}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-1">
                        <button
                          onClick={() => openViewModal(species)}
                          className="text-slate-400 hover:text-cyan-600"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openPhotoModal(species)}
                          className="text-slate-400 hover:text-cyan-600"
                          title="View Photos"
                        >
                          <PhotoIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openEditModal(species)}
                          className="text-slate-400 hover:text-cyan-600"
                          title="Edit"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => openDeleteModal(species)}
                          className="text-slate-400 hover:text-red-600"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {filteredSpecies.length === 0 && (
        <EmptyState
          title="No species found"
          description={
            searchTerm || Object.keys(filters).length > 0
              ? 'Try changing your search keywords or filters.'
              : 'Start by adding your first species profile.'
          }
          icon={<PhotoIcon className="h-6 w-6" />}
          action={
            !searchTerm && Object.keys(filters).length === 0 ? (
              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                <PlusIcon className="h-5 w-5" />
                Add Species
              </button>
            ) : undefined
          }
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeftIcon className="h-5 w-5" />
                </button>
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (page === currentPage || page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          page === currentPage
                            ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                            : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-slate-700">...</span>;
                  }
                  return null;
                })}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRightIcon className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddSpeciesModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddSpecies}
      />

      <SpeciesEnrichmentModal
        isOpen={showEnrichModal}
        onClose={() => setShowEnrichModal(false)}
        onCompleted={loadSpecies}
      />

      {selectedSpecies && (
        <>
          <EditSpeciesModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedSpecies(null);
            }}
            onEdit={handleEditSpecies}
            species={selectedSpecies}
          />

          <SpeciesPhotoGallery
            isOpen={showPhotoModal}
            onClose={() => {
              setShowPhotoModal(false);
              setSelectedSpecies(null);
            }}
            species={selectedSpecies}
            onAvatarUpdate={loadSpecies}
          />

          {/* Species Details Modal */}
          <BrandModal open={showViewModal} onClose={() => setShowViewModal(false)} maxWidthClass="max-w-4xl">
            <BrandModalHeader
              title={`${selectedSpecies?.commonName} Details`}
              onClose={() => setShowViewModal(false)}
            />
            <BrandModalBody>
              {selectedSpecies && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Basic Information</h4>
                    <dl className="space-y-2">
                      <div><dt className="text-sm font-medium text-slate-500">Scientific Name</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.fullTaxonomicName)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Type Category</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.typeCategory)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Origin Location</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.originLocation)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Size</dt><dd className="mt-1 text-sm text-slate-900">{selectedSpecies.size != null ? `${selectedSpecies.size} cm` : '-'}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Weight</dt><dd className="mt-1 text-sm text-slate-900">{selectedSpecies.weight != null ? `${selectedSpecies.weight} g` : '-'}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Longevity</dt><dd className="mt-1 text-sm text-slate-900">{formatYears(selectedSpecies.longevity)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Band Size</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.bandSize)}</dd></div>
                    </dl>
                  </div>

                  {/* Taxonomy */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Taxonomy</h4>
                    <dl className="space-y-2">
                      {TAXONOMY_RANKS.map(({ key, label }) => {
                        const value = selectedSpecies[key as keyof Species];
                        if (key === 'subspecies' && !value) return null;
                        return (
                          <div key={key}>
                            <dt className="text-sm font-medium text-slate-500">{label}</dt>
                            <dd className="mt-1 text-sm text-slate-900">{displayText(value as string)}</dd>
                          </div>
                        );
                      })}
                    </dl>
                  </div>

                  {/* Breeding Information */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Breeding Information</h4>
                    <dl className="space-y-2">
                      <div><dt className="text-sm font-medium text-slate-500">Breeding Season</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.breedingSeasonStart)} – {displayText(selectedSpecies.breedingSeasonEnd)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Incubation Period</dt><dd className="mt-1 text-sm text-slate-900">{formatIncubationDays(selectedSpecies)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Days Before Banding</dt><dd className="mt-1 text-sm text-slate-900">{formatDays(selectedSpecies.daysBeforeBanding)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Nesting Behavior</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.nestingBehavior)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Parental Care Period</dt><dd className="mt-1 text-sm text-slate-900">{formatDays(selectedSpecies.parentalCarePeriod)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Sexual Maturity Age</dt><dd className="mt-1 text-sm text-slate-900">{formatSexualMaturityMonths(selectedSpecies.sexualMaturityAge)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Breeding Difficulty</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.breedingDifficultyLevel)}</dd></div>
                    </dl>
                  </div>

                  {/* Health & Care Information */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Health & Care</h4>
                    <dl className="space-y-2">
                      <div><dt className="text-sm font-medium text-slate-500">Diet</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.diet)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Housing</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.recommendedHousing)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Common Health Issues</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.commonHealthIssues)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Quarantine Period</dt><dd className="mt-1 text-sm text-slate-900">{formatDays(selectedSpecies.quarantinePeriod)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Handling Guidelines</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.handlingGuidelines)}</dd></div>
                    </dl>
                  </div>

                  {/* Market & Sales Information */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">Market & Sales</h4>
                    <dl className="space-y-2">
                      <div><dt className="text-sm font-medium text-slate-500">Price Range</dt><dd className="mt-1 text-sm text-slate-900">{formatPriceRange(selectedSpecies.priceRangeMin, selectedSpecies.priceRangeMax, formatCurrency)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Market Demand Level</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.marketDemandLevel)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Export Restrictions</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.exportRestrictions)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Market Trends</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.marketTrends)}</dd></div>
                    </dl>
                  </div>

                  {/* AI Metadata */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-900 mb-3">AI Metadata</h4>
                    <dl className="space-y-2">
                      <div><dt className="text-sm font-medium text-slate-500">AI Data Sources</dt><dd className="mt-1 text-sm text-slate-900">{selectedSpecies.aiDataSources?.length ? selectedSpecies.aiDataSources.join(', ') : '-'}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">AI Confidence Score</dt><dd className="mt-1 text-sm text-slate-900">{selectedSpecies.aiConfidenceScore != null ? `${(Number(selectedSpecies.aiConfidenceScore) * 100).toFixed(0)}%` : '-'}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Last AI Update</dt><dd className="mt-1 text-sm text-slate-900">{formatAiLastUpdated(selectedSpecies)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">AI Extraction Notes</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.aiExtractionNotes)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">Manual Review Status</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.manualReviewStatus)}</dd></div>
                      <div><dt className="text-sm font-medium text-slate-500">AI Processing Version</dt><dd className="mt-1 text-sm text-slate-900">{displayText(selectedSpecies.aiProcessingVersion)}</dd></div>
                    </dl>
                  </div>

                  <div className="md:col-span-2">
                    <SpeciesMutationCatalogPanel
                      speciesId={selectedSpecies.id}
                      compact
                      onMutationsSynced={(names) =>
                        setSelectedSpecies((prev) =>
                          prev ? { ...prev, mutations: names } : prev,
                        )
                      }
                    />
                  </div>
                </div>
              )}
            </BrandModalBody>
            <BrandModalFooter>
              <button
                type="button"
                className="btn btn-primary text-sm"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </BrandModalFooter>
          </BrandModal>

          {/* Delete Confirmation Modal */}
          <BrandModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidthClass="max-w-lg">
            <BrandModalHeader title="Delete Species" onClose={() => setShowDeleteModal(false)} />
            <BrandModalBody>
              <p className="text-sm text-slate-600">
                Are you sure you want to delete &quot;{selectedSpecies?.commonName}&quot;? This action cannot be undone.
              </p>
            </BrandModalBody>
            <BrandModalFooter>
              <button
                type="button"
                className="btn btn-secondary text-sm"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger text-sm"
                onClick={handleDeleteSpecies}
              >
                Delete
              </button>
            </BrandModalFooter>
          </BrandModal>
        </>
      )}
    </div>
  );
} 